import { Fragment, useEffect, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../components'
import { RECURRENCES, shiftOneYear } from './utils'

const T_PREFIX = 'campaigns'
const INPUT = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500'

const TITLES = { create: 'create_title', edit: 'edit_title', duplicate: 'duplicate_title' }

// Duplicate = create prefilled from the source row with both dates moved one year; edit keeps the row's own dates.
const formFrom = (mode, initial) => {
  if (!initial) return { name: '', starts_on: '', ends_on: '', recurrence: 'none' }
  const shift = mode === 'duplicate' ? shiftOneYear : d => d
  return { name: initial.name, starts_on: shift(initial.starts_on), ends_on: shift(initial.ends_on), recurrence: initial.recurrence || 'none' }
}

export const CampaignFormModal = ({
  open,
  setOpen,
  mode = 'create',
  country,
  initial = null,
  onSubmit,
  isLoading = false,
  serverError = null,
  onClearServerError = () => {},
}) => {
  const { i18n } = useTranslation()
  const nameRef = useRef(null)
  const [form, setForm] = useState(formFrom(mode, initial))
  const [dateError, setDateError] = useState(false)

  useEffect(() => {
    if (!open) return
    setForm(formFrom(mode, initial))
    setDateError(false)
  }, [open, mode, initial])

  const set = field => e => {
    const { value } = e.target
    setForm(prev => ({ ...prev, [field]: value }))
    setDateError(false)
    if (serverError) onClearServerError()
  }

  const handleSubmit = e => {
    e.preventDefault()
    const name = form.name.trim()
    if (!name || !form.starts_on || !form.ends_on) return
    if (form.ends_on < form.starts_on) {
      setDateError(true)
      return
    }
    const payload = { name, starts_on: form.starts_on, ends_on: form.ends_on, recurrence: form.recurrence }
    onSubmit(mode === 'edit' ? payload : { country, ...payload })
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" initialFocus={nameRef} onClose={() => !isLoading && setOpen(false)}>
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-5 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <Dialog.Title className="text-lg font-semibold text-gray-900">{i18n.t(`${T_PREFIX}.form.${TITLES[mode]}`)}</Dialog.Title>
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={isLoading}
                className="text-gray-400 hover:text-gray-600"
                aria-label={i18n.t(`${T_PREFIX}.form.cancel`)}
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            {mode === 'duplicate' && initial && (
              <p className="mb-3 text-sm text-gray-600">{i18n.t(`${T_PREFIX}.form.duplicate_hint`, { name: initial.name })}</p>
            )}
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3">
              {mode !== 'edit' && (
                <div className="col-span-2 text-sm text-gray-600">
                  {i18n.t(`${T_PREFIX}.form.country`)}: <span className="font-medium text-gray-900">{i18n.t(`${T_PREFIX}.countries.${country}`)}</span>
                </div>
              )}
              <label className="col-span-2 flex flex-col gap-1 text-xs text-gray-600">
                {i18n.t(`${T_PREFIX}.form.name`)}
                <input
                  ref={nameRef}
                  type="text"
                  className={INPUT}
                  maxLength={60}
                  placeholder={i18n.t(`${T_PREFIX}.form.name_placeholder`)}
                  value={form.name}
                  onChange={set('name')}
                  required
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                {i18n.t(`${T_PREFIX}.form.starts_on`)}
                <input type="date" className={INPUT} value={form.starts_on} onChange={set('starts_on')} required />
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-600">
                {i18n.t(`${T_PREFIX}.form.ends_on`)}
                <input type="date" className={INPUT} value={form.ends_on} onChange={set('ends_on')} required />
              </label>
              <label className="col-span-2 flex flex-col gap-1 text-xs text-gray-600">
                {i18n.t(`${T_PREFIX}.form.recurrence`)}
                <select className={`${INPUT} pr-10`} value={form.recurrence} onChange={set('recurrence')}>
                  {RECURRENCES.map(r => (
                    <option key={r} value={r}>
                      {i18n.t(`${T_PREFIX}.recurrence.${r}`)}
                    </option>
                  ))}
                </select>
              </label>
              {dateError && <p className="col-span-2 text-xs text-red-700">{i18n.t(`${T_PREFIX}.errors.end_before_start`)}</p>}
              {serverError && <p className="col-span-2 text-xs text-red-700">{serverError}</p>}
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <Button type="secondary" onClick={() => setOpen(false)} disabled={isLoading}>
                  {i18n.t(`${T_PREFIX}.form.cancel`)}
                </Button>
                <Button type="primary" buttonType="submit" loading={isLoading} loading_label={i18n.t(`${T_PREFIX}.form.saving`)}>
                  {i18n.t(`${T_PREFIX}.form.save`)}
                </Button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
